/**
 * API client for the feat-362 new-hire tracker.
 *
 * Backend: `restaurant-backend/routes/admin_new_hires.py`. The wire contract is
 * `CONTRACT.md` r3 in the feat-362 feature root; every shape and status code
 * below is from it. If something here disagrees with that file, that file wins.
 *
 * ALL FOUR ROUTES ARE `require_admin` (Corporate / Manager get 403). There is no
 * tenant parameter: rows are always the pilot tenant's. The SPA half of the gate
 * is `<AdminRoute>` on `/restaurant/administration/new-hires`.
 *
 * Every timestamp is UTC ISO-8601 with a trailing `Z`. Format it in the row's
 * `location.timezone`, never the browser's. `indicator` is computed by the
 * server; never recompute it here.
 *
 * Square names and admin notes are user-entered text: render as plain text only.
 */

import { config } from '../config/environment';
import { fetchApi } from '../utils/api';
import { authHeader } from './authToken';
import {
  fakeDismissNewHire,
  fakeFollowUpNewHire,
  fakeListNewHires,
  fakeUndismissNewHire,
} from './fakeNewHireData';

function getAuthHeaders(): Record<string, string> {
  return authHeader();
}

// ============================================================
// Types — CONTRACT.md r3
// ============================================================

export type NewHireStatus = 'needs_followup' | 'followed_up' | 'recording' | 'dismissed';
export type NewHireIndicator = 'red' | 'amber' | 'neutral' | 'green' | 'grey';
export type NewHireReason = 'unmapped' | 'mapped_never_recorded';
export type NewHireReviewReason = 'name_match' | 'name_taken' | 'no_name' | 'inactive_mapping';

export interface NewHireCandidate {
  server_id: string;
  server_name: string;
  /** The candidate SERVER's location (link target). r2. */
  location_id: string;
}

/** Learn activity for the linked server, all-time, any location. r3. */
export interface NewHireTraining {
  /** learn_drill_completion rows. */
  lessons_completed: number;
  /** MAX(first_completed_at). */
  last_completed_at: string | null;
  /** Any Learn activity (views, answers, attempts, completions). */
  started: boolean;
  last_activity_at: string | null;
}

/** r3. */
export interface NewHireOnboarding {
  onboarding_complete: boolean;
  consent_given: boolean;
  consent_given_at: string | null;
}

export interface NewHireRow {
  id: string;
  /** The row's tenant (always L&L). r2. */
  tenant_id: string;
  square: {
    display_name: string | null;
    team_member_id: string;
    first_clock_in_at: string | null;
    last_clock_in_at: string | null;
  };
  /** The ASSIGNED store. Never null. */
  location: {
    id: string;
    name: string;
    /** IANA. Use for ALL formatting of this row. */
    timezone: string;
  };
  reason: NewHireReason;
  server: {
    id: string;
    name: string;
    tenant_id: string;
    /** The SERVER's own location (link target). */
    location_id: string;
    has_phone: boolean;
    /** The SERVER's store name ("" if unknown). r3. */
    location_name: string;
  } | null;
  /** True = this job created `server`. May be true with `server === null` — show no badge then. */
  auto_created: boolean;
  would_create: boolean;
  /** Exception class name of the last failed auto-create, or null. */
  create_error: string | null;
  needs_review: boolean;
  review_reason: NewHireReviewReason | null;
  /** Never null; [] unless needs_review. */
  candidates: NewHireCandidate[];
  bespoke: {
    first_shift_at: string | null;
    last_shift_at: string | null;
    /** r3. Null iff `server === null`. */
    training: NewHireTraining | null;
    /** r3. Null iff `server === null`. */
    onboarding: NewHireOnboarding | null;
  };
  status: NewHireStatus;
  indicator: NewHireIndicator;
  recorded_at: string | null;
  followed_up: { by: string; at: string; note: string } | null;
  dismissed: { by: string; at: string; reason: string } | null;
  first_detected_at: string;
}

export interface NewHireList {
  as_of: string;
  followup_amber_days: number;
  /** Already sorted by the server (indicator rank, then newest detection). */
  rows: NewHireRow[];
}

// ============================================================
// API
// ============================================================

const BASE = '/api/admin/new-hires';

export async function listNewHires(): Promise<NewHireList> {
  if (config.useDummyData) return fakeListNewHires();
  const data = await fetchApi<NewHireList>(BASE, { headers: getAuthHeaders() });
  return { ...data, rows: data.rows ?? [] };
}

/** `note` is required (400 if blank after trim, or over 1000 chars). */
export async function followUpNewHire(id: string, note: string): Promise<NewHireRow> {
  if (config.useDummyData) return fakeFollowUpNewHire(id, note);
  return fetchApi<NewHireRow>(`${BASE}/${encodeURIComponent(id)}/follow-up`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: { note },
  });
}

/** `reason` blank or omitted → server default "Doesn't need a mic". Max 255. */
export async function dismissNewHire(id: string, reason?: string | null): Promise<NewHireRow> {
  if (config.useDummyData) return fakeDismissNewHire(id, reason ?? null);
  return fetchApi<NewHireRow>(`${BASE}/${encodeURIComponent(id)}/dismiss`, {
    method: 'POST',
    headers: getAuthHeaders(),
    // fetchApi drops a falsy body, so always send an object.
    body: { reason: reason ?? null },
  });
}

export async function undismissNewHire(id: string): Promise<NewHireRow> {
  if (config.useDummyData) return fakeUndismissNewHire(id);
  return fetchApi<NewHireRow>(`${BASE}/${encodeURIComponent(id)}/undismiss`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
}
